import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { sendReceiptByEmail, sendReceiptByText } from 'api/Transaction';
import {
  emailDrClearCareEstimate,
  emailGoodFaithEstimate,
  textDrClearCareEstimate,
  textGoodFaithEstimate
} from 'api/Appointment';

import { recordReceiptDelivery } from 'lib/helpers/receiptDelivery';

import { showAlert } from 'components/shared/Alert/Alert';
import {
  sendEmail as sendInvoiceEmail,
  sendText as sendInvoiceText
} from 'components/practice/BeyondBilling/ElectronicInvoicing/components/Invoice/components/InvoiceShare/lib/utils';

import { DOCUMENT_IDS, DOCUMENTS, invoiceDocumentId, invoiceIdFromDocumentId } from './constants';

const documentName = (id) => {
  const invoiceId = invoiceIdFromDocumentId(id);
  if (invoiceId) return `Invoice #${invoiceId}`;

  return DOCUMENTS.find((document) => document.id === id)?.name || 'Document';
};

const invoiceSender = (navigate, id) => ({
  email: (contact) =>
    sendInvoiceEmail({
      id,
      navigate,
      toAltEmail: contact.toAltEmail,
      customEmail: contact.customEmail ?? null
    }),
  text: (contact) =>
    sendInvoiceText({
      id,
      navigate,
      toAltPhone: contact.toAltPhone,
      customPhone: contact.customPhone ?? null
    })
});

const buildSenders = ({
  navigate,
  appointmentId,
  transactionId,
  invoiceId,
  invoiceIds = [],
  token
}) => {
  // The GFE and the GFE-with-payment-options selections are delivered by the same endpoint, so
  // they share one sender. `send` dedupes by sender identity to avoid a duplicate request when
  // both are selected (Select all does exactly that).
  const goodFaithEstimate = {
    email: (contact) => emailGoodFaithEstimate(navigate, { appointmentId, ...contact }),
    text: (contact) => textGoodFaithEstimate(navigate, { appointmentId, ...contact })
  };

  return {
    [DOCUMENT_IDS.RECEIPT]: {
      email: (contact) =>
        sendReceiptByEmail(navigate, { transactionId, appointmentId, token, ...contact }),
      text: (contact) =>
        sendReceiptByText(navigate, { transactionId, appointmentId, token, ...contact })
    },
    [DOCUMENT_IDS.GOOD_FAITH_ESTIMATE]: goodFaithEstimate,
    [DOCUMENT_IDS.GFE_PAYMENT_OPTIONS]: goodFaithEstimate,
    [DOCUMENT_IDS.INVOICE]: {
      email: (contact) =>
        invoiceId
          ? sendInvoiceEmail({
              id: invoiceId,
              navigate,
              toAltEmail: contact.toAltEmail,
              customEmail: contact.customEmail ?? null
            })
          : null,
      text: (contact) =>
        invoiceId
          ? sendInvoiceText({
              id: invoiceId,
              navigate,
              toAltPhone: contact.toAltPhone,
              customPhone: contact.customPhone ?? null
            })
          : null
    },
    [DOCUMENT_IDS.DR_CLEAR_CARE]: {
      email: (contact) => emailDrClearCareEstimate(navigate, { appointmentId, ...contact }),
      text: (contact) => textDrClearCareEstimate(navigate, { appointmentId, ...contact })
    },
    ...invoiceIds.reduce(
      (acc, id) => ({ ...acc, [invoiceDocumentId(id)]: invoiceSender(navigate, id) }),
      {}
    )
  };
};

export const useDocumentDelivery = ({
  selectedIds = [],
  appointmentId,
  transactionId,
  invoiceId,
  invoiceIds = [],
  token
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(null);

  const send = async (channel, contact = {}) => {
    if (busy || !selectedIds.length) return;

    const senders = buildSenders({
      navigate,
      appointmentId,
      transactionId,
      invoiceId,
      invoiceIds,
      token
    });
    const requested = selectedIds.map((id) => ({ id, request: senders[id]?.[channel] }));
    const unsupported = requested.filter((entry) => !entry.request);

    const seenRequests = new Set();
    const supported = requested.filter((entry) => {
      if (!entry.request || seenRequests.has(entry.request)) return false;
      seenRequests.add(entry.request);
      return true;
    });

    if (!supported.length) {
      showAlert({
        color: 'warning',
        title: `Can't send by ${channel}`,
        message: `${unsupported.map((entry) => documentName(entry.id)).join(', ')} can't be sent by ${channel}.`
      });
      return;
    }

    setBusy(channel);
    const results = await Promise.allSettled(
      supported.map(
        (entry) =>
          entry.request(contact) ??
          Promise.reject(new Error(`${documentName(entry.id)} could not be sent by ${channel}`))
      )
    );
    setBusy(null);

    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value?.delivery)
        recordReceiptDelivery(queryClient, result.value.delivery);
    });

    const failed = supported.filter((entry, index) => {
      const result = results[index];
      return result.status === 'rejected' || result.value?.code !== 0;
    });

    if (failed.length === supported.length) {
      showAlert({ color: 'danger', title: `Couldn't send the documents by ${channel}` });
      return;
    }

    const skipped = [...failed, ...unsupported].map((entry) => documentName(entry.id));

    showAlert({
      color: skipped.length ? 'warning' : 'success',
      title:
        channel === 'email'
          ? 'Documents sent to email successfully.'
          : 'Documents sent to phone number successfully.',
      message: skipped.length ? `Not sent: ${skipped.join(', ')}` : undefined
    });
  };

  return { busy, send };
};
